import { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { ArrowRight, LockKeyhole, Mail, UserRound, X } from 'lucide-react';
import { auth, db, firebaseIsConfigured } from '../lib/firebase';

const readableAuthError = (error) => {
  const code = error?.code || '';
  if (code.includes('invalid-credential')) return 'The email or password is incorrect.';
  if (code.includes('email-already-in-use')) return 'An account already exists for this email.';
  if (code.includes('weak-password')) return 'Use a password with at least six characters.';
  if (code.includes('invalid-email')) return 'Enter a valid email address.';
  if (code.includes('too-many-requests')) return 'Too many attempts. Please wait and try again.';
  return 'Something went wrong. Please try again.';
};

export default function AuthPanel({ mode, onClose, onModeChange }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const isSignUp = mode === 'signup';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });

    if (!firebaseIsConfigured || !auth || !db) {
      setStatus({ type: 'error', message: 'Firebase environment variables are not configured yet.' });
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const credential = await createUserWithEmailAndPassword(auth, email.trim(), password);
        if (name.trim()) await updateProfile(credential.user, { displayName: name.trim() });
        await setDoc(doc(db, 'users', credential.user.uid), {
          email: credential.user.email,
          displayName: name.trim() || 'Maker',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          onboardingComplete: false,
        }, { merge: true });
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
      onClose();
    } catch (error) {
      setStatus({ type: 'error', message: readableAuthError(error) });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!email.trim()) {
      setStatus({ type: 'error', message: 'Enter your email first, then choose reset password.' });
      return;
    }
    if (!auth) return;
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setStatus({ type: 'success', message: 'Password reset email sent.' });
    } catch (error) {
      setStatus({ type: 'error', message: readableAuthError(error) });
    }
  };

  return (
    <div className="auth-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section className="auth-panel" role="dialog" aria-modal="true" aria-labelledby="auth-title">
        <button className="icon-button auth-panel__close" type="button" onClick={onClose} aria-label="Close account panel">
          <X size={20} />
        </button>
        <p className="eyebrow">Maker account</p>
        <h2 id="auth-title">{isSignUp ? 'Build smarter margins.' : 'Welcome back.'}</h2>
        <p className="auth-panel__intro">
          {isSignUp ? 'Save calculators, cost profiles, and product pricing in one place.' : 'Sign in to continue building your catalog.'}
        </p>

        <form onSubmit={handleSubmit}>
          {isSignUp && (
            <label className="field">
              <span>Name</span>
              <span className="field__control"><UserRound size={18} /><input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="Full name" /></span>
            </label>
          )}
          <label className="field">
            <span>Email</span>
            <span className="field__control"><Mail size={18} /><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" /></span>
          </label>
          <label className="field">
            <span>Password</span>
            <span className="field__control"><LockKeyhole size={18} /><input required minLength={6} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={isSignUp ? 'new-password' : 'current-password'} placeholder="At least 6 characters" /></span>
          </label>

          {status.message && <p className={`form-status form-status--${status.type}`}>{status.message}</p>}

          <button className="button button--primary button--full" disabled={loading} type="submit">
            {loading ? 'Working…' : isSignUp ? 'Create account' : 'Sign in'} <ArrowRight size={18} />
          </button>
        </form>

        {!isSignUp && <button className="text-button" type="button" onClick={handleReset}>Forgot password?</button>}
        <div className="auth-panel__switch">
          {isSignUp ? 'Already have an account?' : 'New to MakerMargin?'}
          <button type="button" onClick={() => onModeChange(isSignUp ? 'signin' : 'signup')}>{isSignUp ? 'Sign in' : 'Create one'}</button>
        </div>
      </section>
    </div>
  );
}
